import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type MouseEvent,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import type {
  CrmAccountListItem,
  CrmAccountStatus,
  CrmNextAction,
  Timestamp,
} from "@shared/modules/crm";
import { api, ApiError } from "../../../core/lib/api";
import { PageHeader } from "../../../core/components/ui/page-header";
import { Button } from "../../../core/components/ui/button";
import { Icon } from "../../../core/components/ui/icon";
import { Input, Select } from "../../../core/components/ui/input";
import { useToastStore } from "../../../core/stores/toast";
import { CrmStatusBadge } from "../components/status-badge";
import { AccountQuickCreate } from "../components/account-quick-create";
import { LabelOptions } from "../components/label-options";
import { Money } from "../components/money";
import {
  CRM_PARTNER_LABELS,
  CRM_SECTOR_LABELS,
  CRM_STATUS_LABELS,
  CRM_STATUS_ORDER,
  formatCents,
  formatRelativeDay,
  partnerLabel,
  sectorLabel,
  type AdminUser,
} from "../lib/crm";

type CrmView = "kanban" | "liste";
type Failure = "forbidden" | "error" | null;
type CrmSort = "recent" | "name" | "mrr" | "next";

const VIEW_STORAGE_KEY = "bp-crm-view";

// Type MIME propre au tableau. La charge de glissement ne transporte QUE
// l'identifiant du dossier : tant que la carte était un <a>, le navigateur y
// ajoutait l'URL de la fiche, et un dépôt manqué se transformait en collage
// dans le champ de recherche ou en navigation vers la fiche.
const DRAG_MIME = "application/x-crm-account";

const SORT_LABELS: Record<CrmSort, string> = {
  recent: "Activité récente",
  name: "Nom",
  mrr: "MRR décroissant",
  next: "Prochaine action",
};

/** Carte en cours de glissement : son identité et sa colonne de départ. */
interface DragState {
  id: string;
  from: CrmAccountStatus;
}

/**
 * Traces de la perte d'un dossier. Le serveur les efface dès qu'on quitte
 * « Perdu » : sans les avoir mises de côté, « Annuler » ramènerait bien la
 * carte dans sa colonne mais avec une date de perte remise à aujourd'hui et un
 * motif définitivement vide.
 */
interface LostTrace {
  lostAt: Timestamp | null;
  lostReason: string | null;
}

interface MoveOptions {
  undoable: boolean;
  /** Valeurs de perte à réécrire explicitement (annulation d'une sortie de « Perdu »). */
  restore?: LostTrace;
}

export function CrmPipeline() {
  const navigate = useNavigate();
  const push = useToastStore((s) => s.push);
  const [view, setView] = useState<CrmView>(readStoredView);
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [owner, setOwner] = useState("");
  const [partner, setPartner] = useState("");
  const [sector, setSector] = useState("");
  const [sort, setSort] = useState<CrmSort>("recent");

  const [accounts, setAccounts] = useState<CrmAccountListItem[]>([]);
  const [owners, setOwners] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState<Failure>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const [drag, setDrag] = useState<DragState | null>(null);
  const [overStatus, setOverStatus] = useState<CrmAccountStatus | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const [focusRequest, setFocusRequest] = useState<string | null>(null);

  // Un jeton par dossier, incrémenté à chaque déplacement : il départage la
  // réponse du PATCH en cours de celle d'un PATCH que l'utilisateur a déjà
  // rendu caduc en redéplaçant la carte.
  const moveTokens = useRef(new Map<string, number>());
  // Une file par dossier : deux déplacements rapides doivent atteindre le
  // serveur dans l'ordre où ils ont été faits, sinon la base retient le premier.
  const moveQueue = useRef(new Map<string, Promise<void>>());
  // Photo de la liste lisible hors du rendu : ce qu'une annulation doit rendre
  // se lit AVANT que la mise à jour optimiste ne l'ait effacé.
  const accountsRef = useRef<CrmAccountListItem[]>([]);
  useEffect(() => {
    accountsRef.current = accounts;
  }, [accounts]);

  // La recherche est filtrée côté serveur : on attend une pause de frappe
  // plutôt que de lancer une requête par touche.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(q.trim()), 250);
    return () => clearTimeout(timer);
  }, [q]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (owner) params.set("owner", owner);
    if (partner) params.set("partner", partner);
    if (sector) params.set("sector", sector);
    if (search) params.set("q", search);
    params.set("sort", sort);

    let cancelled = false;
    setLoading(true);
    api<CrmAccountListItem[]>(`/api/crm/accounts?${params.toString()}`)
      .then((list) => {
        if (cancelled) return;
        setAccounts(list);
        setFailure(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Une panne ne doit pas se déguiser en filtre trop étroit : seul un 403
        // relève de l'accès réservé, le reste est un échec à annoncer comme tel.
        setAccounts([]);
        setFailure(err instanceof ApiError && err.status === 403 ? "forbidden" : "error");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [status, owner, partner, sector, search, sort, reloadKey]);

  useEffect(() => {
    api<AdminUser[]>("/api/users")
      .then((list) => setOwners(list.filter((u) => u.role === "admin")))
      .catch(() => setOwners([]));
  }, []);

  const columns = useMemo(
    () =>
      CRM_STATUS_ORDER.map((columnStatus) => {
        const items = accounts.filter((a) => a.status === columnStatus);
        return {
          status: columnStatus,
          items,
          mrrCents: items.reduce((total, a) => total + a.mrrCents, 0),
        };
      }),
    [accounts],
  );

  const selectView = (next: CrmView) => {
    setView(next);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      // stockage indisponible : la préférence ne survit pas au rechargement, c'est tout
    }
  };

  /**
   * Chemin unique de changement de statut : glisser-déposer, sélecteur clavier
   * et bouton « Annuler » passent tous par ici. La carte bouge d'abord (le geste
   * doit répondre tout de suite), le serveur confirme ensuite ; s'il refuse,
   * la carte retourne d'où elle vient et la liste est redemandée au serveur.
   */
  const moveAccount = (
    id: string,
    name: string,
    from: CrmAccountStatus,
    to: CrmAccountStatus,
    options: MoveOptions,
  ): void => {
    if (from === to) return;

    const token = (moveTokens.current.get(id) ?? 0) + 1;
    moveTokens.current.set(id, token);

    const before = accountsRef.current.find((a) => a.id === id);
    const lostBefore: LostTrace = {
      lostAt: before?.lostAt ?? null,
      lostReason: before?.lostReason ?? null,
    };

    setAccounts((list) => list.map((a) => (a.id === id ? withStatus(a, to) : a)));
    setAnnouncement(
      options.undoable
        ? `${name} déplacé vers ${CRM_STATUS_LABELS[to]}. Annulation disponible pendant huit secondes dans la notification, en fin de page.`
        : `${name} déplacé vers ${CRM_STATUS_LABELS[to]}.`,
    );

    const body: Record<string, unknown> = { status: to };
    if (options.restore) {
      body.lostAt = toIsoOrNull(options.restore.lostAt);
      body.lostReason = options.restore.lostReason;
    }

    const request = (moveQueue.current.get(id) ?? Promise.resolve()).then(() =>
      api(`/api/crm/accounts/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    );
    moveQueue.current.set(
      id,
      request.then(
        () => undefined,
        () => undefined,
      ),
    );

    request
      .then(() => {
        if (moveTokens.current.get(id) !== token) return;
        push(
          "info",
          `${name} → ${CRM_STATUS_LABELS[to]}`,
          undefined,
          options.undoable
            ? {
                label: "Annuler",
                onClick: () => {
                  // Une notification vit huit secondes : de quoi survivre au
                  // déplacement suivant. Elle n'annule que le geste qu'elle
                  // annonce, jamais celui qui l'a remplacé.
                  if (moveTokens.current.get(id) !== token) {
                    push(
                      "warning",
                      "Annulation impossible",
                      `${name} a été déplacé depuis : c'est son statut actuel qui fait foi.`,
                    );
                    return;
                  }
                  moveAccount(id, name, to, from, {
                    undoable: false,
                    // Retour dans « Perdu » : on réécrit la date et le motif
                    // d'origine, que le serveur avait effacés à l'aller.
                    restore: from === "perdu" ? lostBefore : undefined,
                  });
                },
              }
            : undefined,
        );
      })
      .catch((err: unknown) => {
        if (moveTokens.current.get(id) !== token) return;
        setAccounts((list) => list.map((a) => (a.id === id ? withStatus(a, from) : a)));
        const forbidden = err instanceof ApiError && err.status === 403;
        push(
          "danger",
          "Déplacement refusé",
          forbidden
            ? "Le CRM est réservé aux administrateurs."
            : `${name} reste dans « ${CRM_STATUS_LABELS[from]} » — réessayez dans un instant.`,
        );
        setAnnouncement(`Déplacement refusé : ${name} reste dans ${CRM_STATUS_LABELS[from]}.`);
        // Le retour en arrière local n'est qu'une supposition : on redemande la
        // liste au serveur plutôt que de laisser le tableau affirmer un statut.
        setReloadKey((k) => k + 1);
      });
  };

  const endDrag = () => {
    setDrag(null);
    setOverStatus(null);
  };

  const clearFocusRequest = useCallback(() => setFocusRequest(null), []);

  const handleDrop = (event: DragEvent<HTMLElement>, to: CrmAccountStatus) => {
    event.preventDefault();
    // Le presse-papiers de glissement fait foi ; l'état local sert de secours
    // si le navigateur n'a rien transmis.
    const id = event.dataTransfer.getData(DRAG_MIME) || drag?.id || "";
    endDrag();
    const account = accounts.find((a) => a.id === id);
    if (!account || account.status === to) return;
    moveAccount(account.id, account.name, account.status, to, { undoable: true });
  };

  return (
    <div className="page">
      <PageHeader
        title="Pipeline"
        subtitle="Tous les dossiers, du premier contact jusqu'à la mise en service."
        actions={
          failure === "forbidden" ? undefined : (
            <Button variant="primary" icon={<Icon name="plus" size={16} />} onClick={() => setFormOpen(true)}>
              Nouveau dossier
            </Button>
          )
        }
      />

      {failure === "forbidden" ? (
        <p className="crm-empty">
          Le CRM est réservé aux administrateurs. Demandez un accès admin pour consulter les
          dossiers.
        </p>
      ) : (
        <>
          <div className="crm-toolbar">
            <Input
              className="crm-toolbar__search"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher un dossier, un contact, une ville…"
              aria-label="Rechercher un dossier"
            />
            <div className="crm-toolbar__filters">
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                aria-label="Filtrer par statut"
              >
                <option value="">Tous les statuts</option>
                <LabelOptions labels={CRM_STATUS_LABELS} />
              </Select>
              <Select
                value={owner}
                onChange={(e) => setOwner(e.target.value)}
                aria-label="Filtrer par responsable"
              >
                <option value="">Tous les responsables</option>
                {owners.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </Select>
              <Select
                value={partner}
                onChange={(e) => setPartner(e.target.value)}
                aria-label="Filtrer par partenaire"
              >
                <option value="">Tous les partenaires</option>
                <LabelOptions labels={CRM_PARTNER_LABELS} />
              </Select>
              <Select
                value={sector}
                onChange={(e) => setSector(e.target.value)}
                aria-label="Filtrer par secteur"
              >
                <option value="">Tous les secteurs</option>
                <LabelOptions labels={CRM_SECTOR_LABELS} />
              </Select>
              <Select
                value={sort}
                onChange={(e) => setSort(e.target.value as CrmSort)}
                aria-label="Trier les dossiers"
              >
                <LabelOptions labels={SORT_LABELS} />
              </Select>
            </div>
            <span className="crm-toolbar__spacer" />
            <div className="crm-toolbar__views" role="group" aria-label="Affichage">
              <button
                type="button"
                className={`crm-toolbar__view${view === "kanban" ? " is-active" : ""}`}
                aria-pressed={view === "kanban"}
                onClick={() => selectView("kanban")}
              >
                Kanban
              </button>
              <button
                type="button"
                className={`crm-toolbar__view${view === "liste" ? " is-active" : ""}`}
                aria-pressed={view === "liste"}
                onClick={() => selectView("liste")}
              >
                Liste
              </button>
            </div>
          </div>

          {/* Le déplacement d'une carte n'a aucun reflet visuel pour un lecteur
              d'écran : cette zone, toujours montée, en donne le résultat. */}
          <p className="crm-live" aria-live="polite">
            {announcement}
          </p>

          {loading ? (
            <p className="crm-empty">Chargement des dossiers…</p>
          ) : failure === "error" ? (
            <p className="crm-empty">Dossiers indisponibles — réessayez dans un instant.</p>
          ) : accounts.length === 0 ? (
            <p className="crm-empty">
              Aucun dossier ne correspond à ces critères — élargissez les filtres ou créez un
              nouveau dossier.
            </p>
          ) : view === "kanban" ? (
            <div
              className="crm-board"
              // Attrape-tout : un dépôt raté (entre deux colonnes, sous la
              // dernière carte) doit être un non-événement silencieux, et non
              // le comportement par défaut du navigateur sur la charge lâchée.
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => e.preventDefault()}
            >
              {columns.map((col) => {
                // La colonne de départ n'est pas une cible : on n'y « dépose » pas
                // une carte qui s'y trouve déjà.
                const isTarget = drag !== null && drag.from !== col.status;
                const colClasses = [
                  "crm-board__col",
                  isTarget ? "crm-board__col--target" : null,
                  isTarget && overStatus === col.status ? "crm-board__col--over" : null,
                ]
                  .filter(Boolean)
                  .join(" ");

                return (
                  <section
                    key={col.status}
                    className={colClasses}
                    onDragOver={(e) => {
                      if (!isTarget) return;
                      // Sans preventDefault, le navigateur refuse le dépôt.
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                      if (overStatus !== col.status) setOverStatus(col.status);
                    }}
                    onDragLeave={(e) => {
                      // Passer d'une carte à l'autre déclenche des dragleave :
                      // on ne réagit qu'à une sortie réelle de la colonne.
                      const next = e.relatedTarget as Node | null;
                      if (next && e.currentTarget.contains(next)) return;
                      setOverStatus((s) => (s === col.status ? null : s));
                    }}
                    onDrop={(e) => handleDrop(e, col.status)}
                  >
                    <div className="crm-board__col-head">
                      <span className="crm-board__col-title">
                        {CRM_STATUS_LABELS[col.status]}
                      </span>
                      <span className="crm-board__col-count">{col.items.length}</span>
                      <span className="crm-board__col-sum">{formatCents(col.mrrCents)}</span>
                    </div>
                    <div className="crm-board__cards">
                      {col.items.length === 0 ? (
                        <p className="crm-board__empty">
                          {isTarget ? "Déposer ici" : "Aucun dossier"}
                        </p>
                      ) : (
                        col.items.map((a) => (
                          <BoardCard
                            key={a.id}
                            account={a}
                            dragging={drag?.id === a.id}
                            focusRequested={focusRequest === a.id}
                            onFocusHandled={clearFocusRequest}
                            onDragStart={() => setDrag({ id: a.id, from: a.status })}
                            onDragEnd={endDrag}
                            onMove={(to) => {
                              setFocusRequest(a.id);
                              moveAccount(a.id, a.name, a.status, to, { undoable: true });
                            }}
                          />
                        ))
                      )}
                    </div>
                  </section>
                );
              })}
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Dossier</th>
                  <th>Statut</th>
                  <th>Secteur</th>
                  <th>Responsable</th>
                  <th>Partenaire</th>
                  <th>MRR</th>
                  <th>Encaissé</th>
                  <th>Prochaine action</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((a) => (
                  <tr key={a.id}>
                    <td>{a.name}</td>
                    <td>
                      <CrmStatusBadge status={a.status} />
                    </td>
                    <td>{sectorLabel(a.sector)}</td>
                    <td>{a.ownerName ?? "—"}</td>
                    <td>{partnerLabel(a.partner)}</td>
                    <td>
                      <Money cents={a.mrrCents} currency={a.currency} />
                    </td>
                    <td>
                      <Money cents={a.collectedCents} currency={a.currency} />
                    </td>
                    <td>
                      <NextAction action={a.nextAction} />
                    </td>
                    <td className="table__actions">
                      <Link to={`/crm/dossiers/${a.id}`}>Ouvrir</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      <AccountQuickCreate
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onCreated={(account, openFiche) => {
          setFormOpen(false);
          // Ouvrir la fiche rend le rechargement inutile : on quitte la page.
          if (openFiche) navigate(`/crm/dossiers/${account.id}`);
          else setReloadKey((k) => k + 1);
        }}
      />
    </div>
  );
}

/**
 * La carte est un <article>, plus un <a> englobant : elle porte un sélecteur et
 * un bouton, que la spécification HTML interdit d'imbriquer dans un lien (et
 * que plusieurs lecteurs d'écran n'annonçaient jamais). Le nom reste donc le
 * vrai lien — focus clavier, nouvel onglet, menu contextuel — et le reste de la
 * carte n'ajoute qu'une affordance de clic, comme les lignes de tableau
 * cliquables (voir `.table__row--link`).
 *
 * Le glissement, lui, part de la seule poignée : une carte qui se saisissait
 * partout ne s'ouvrait nulle part.
 */
function BoardCard({
  account,
  dragging,
  focusRequested,
  onFocusHandled,
  onDragStart,
  onDragEnd,
  onMove,
}: {
  account: CrmAccountListItem;
  dragging: boolean;
  focusRequested: boolean;
  onFocusHandled: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onMove: (to: CrmAccountStatus) => void;
}) {
  const navigate = useNavigate();
  // Un glissement se termine parfois par un clic : ce drapeau l'intercepte pour
  // que déplacer une carte n'ouvre pas la fiche par surprise.
  const justDragged = useRef(false);
  // La poignée est minuscule ; c'est la carte qui doit suivre le curseur.
  const cardRef = useRef<HTMLElement>(null);
  const moveRef = useRef<HTMLSelectElement>(null);
  const [pendingStatus, setPendingStatus] = useState<CrmAccountStatus | "">("");

  // Un déplacement démonte la carte de sa colonne pour la remonter dans une
  // autre : sans cela le focus retomberait sur <body>, très loin du bouton
  // « Annuler » de la notification.
  useEffect(() => {
    if (!focusRequested) return;
    moveRef.current?.focus();
    onFocusHandled();
  }, [focusRequested, onFocusHandled]);

  const classes = [
    "crm-card",
    dragging ? "crm-card--dragging" : null,
    account.nextAction?.overdue ? "crm-card--overdue" : null,
    account.tenantId ? "crm-card--linked" : null,
  ]
    .filter(Boolean)
    .join(" ");

  const otherStatuses = CRM_STATUS_ORDER.filter((s) => s !== account.status);

  /**
   * Ouverture de la fiche au clic sur la carte. Le clic appartient d'abord à
   * qui il vise : une commande, un lien, un clic modifié (Cmd+clic ouvre un
   * onglet, Maj une fenêtre), la fin d'un glissement ou une sélection de texte
   * ne doivent jamais se transformer en navigation.
   *
   * La rangée `.crm-card__move` est exclue en entier, pas seulement ses deux
   * commandes : la gouttière qui les sépare avalerait un clic mal ajusté, et
   * Firefox recible sur le conteneur les clics visant le bouton désactivé.
   * La poignée, elle, n'est PAS exclue : le glisser-déposer HTML5 n'existe pas
   * au doigt, et l'ignorer ferait des trois barres — l'élément le plus visible
   * de la carte — une zone morte sur tablette. Le drapeau `justDragged` suffit
   * à absorber le clic qui suit un vrai glissement.
   */
  const handleCardClick = (event: MouseEvent<HTMLElement>) => {
    if (justDragged.current) return;
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (
      (event.target as HTMLElement).closest(
        "a, button, select, input, label, .crm-card__move",
      )
    ) {
      return;
    }
    if (window.getSelection()?.toString()) return;
    navigate(`/crm/dossiers/${account.id}`);
  };

  return (
    <article ref={cardRef} className={classes} onClick={handleCardClick}>
      <div className="crm-card__top">
        {/* Seule zone de saisie de la carte : trois barres, souris uniquement.
            Elle reste hors du parcours clavier — le sélecteur ci-dessous est le
            chemin accessible, et il est visible en permanence. Un simple clic
            (ou un tap, qui n'émet aucun `dragstart`) y ouvre la fiche comme
            partout ailleurs sur la carte. */}
        <span
          className="crm-card__grip"
          aria-hidden="true"
          draggable
          onDragStart={(e) => {
            // Le presse-papiers est vidé d'abord : le navigateur y pré-remplit
            // l'URL de tout lien saisi, et un dépôt manqué déposerait alors un lien.
            e.dataTransfer.clearData();
            e.dataTransfer.setData(DRAG_MIME, account.id);
            e.dataTransfer.effectAllowed = "move";
            // Sans cela, on traînerait la poignée seule : un rectangle de
            // quelques pixels, illisible. Les décalages sont pris depuis le coin
            // de la carte pour qu'elle reste sous le curseur, là où on l'a saisie.
            const card = cardRef.current;
            if (card) {
              const rect = card.getBoundingClientRect();
              e.dataTransfer.setDragImage(card, e.clientX - rect.left, e.clientY - rect.top);
            }
            justDragged.current = true;
            onDragStart();
          }}
          onDragEnd={() => {
            onDragEnd();
            // Le clic de fin de glissement arrive juste après : on ne relâche le
            // drapeau qu'au tour de boucle suivant.
            setTimeout(() => {
              justDragged.current = false;
            }, 0);
          }}
        />
        <Link
          to={`/crm/dossiers/${account.id}`}
          className="crm-card__name"
          // Le nom n'est pas une poignée : un lien se glisse nativement, et le
          // navigateur traînerait son URL au lieu du dossier.
          draggable={false}
          onClick={(e) => {
            if (justDragged.current) e.preventDefault();
          }}
        >
          {account.name}
        </Link>
        <span className="crm-card__sector">{sectorLabel(account.sector)}</span>
      </div>
      <div className="crm-card__meta">
        <span className="crm-card__owner">{account.ownerName ?? "Sans responsable"}</span>
        <span className="crm-card__mrr">{formatCents(account.mrrCents, account.currency)}</span>
      </div>
      <div className="crm-card__next">
        {account.nextAction
          ? `${account.nextAction.title} · ${formatRelativeDay(account.nextAction.dueAt)}`
          : "Aucune action planifiée"}
      </div>
      {/* Le glisser-déposer est hors de portée au clavier et au doigt : le même
          déplacement reste accessible ici. Le choix ne déclenche rien — une
          flèche du clavier suffit à changer la valeur d'un <select> fermé sur
          Firefox et sous Windows — c'est le bouton voisin qui le commet. */}
      <div className="crm-card__move">
        <Select
          ref={moveRef}
          className="crm-card__move-select"
          value={pendingStatus}
          aria-label={`Nouveau statut pour ${account.name}`}
          title={`Déplacer ${account.name} vers un autre statut, sans la souris`}
          onChange={(e) => setPendingStatus(e.target.value as CrmAccountStatus | "")}
        >
          <option value="">Déplacer vers…</option>
          {otherStatuses.map((s) => (
            <option key={s} value={s}>
              {CRM_STATUS_LABELS[s]}
            </option>
          ))}
        </Select>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="crm-card__move-btn"
          disabled={pendingStatus === ""}
          aria-label={`Déplacer ${account.name} vers le statut choisi`}
          onClick={() => {
            if (!pendingStatus) return;
            const to = pendingStatus;
            setPendingStatus("");
            onMove(to);
          }}
        >
          Déplacer
        </Button>
      </div>
    </article>
  );
}

/**
 * Applique un statut à un dossier en reprenant les règles de jalons du serveur
 * (routes/accounts.ts) : quitter « Perdu » efface la date et le motif de
 * perte, y entrer les pose. Sans ce miroir, la liste locale garderait une trace
 * que la base n'a plus, et l'annulation suivante la réécrirait en base.
 */
function withStatus(account: CrmAccountListItem, to: CrmAccountStatus): CrmAccountListItem {
  if (account.status === "perdu" && to !== "perdu") {
    return { ...account, status: to, lostAt: null, lostReason: null };
  }
  if (to === "perdu" && !account.lostAt) {
    return { ...account, status: to, lostAt: new Date().toISOString() };
  }
  return { ...account, status: to };
}

/** L'API n'accepte que de l'ISO 8601 ; les horodatages arrivent aussi en secondes. */
function toIsoOrNull(value: Timestamp | null): string | null {
  if (value === null) return null;
  const date = new Date(typeof value === "number" ? value * 1000 : value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function NextAction({ action }: { action: CrmNextAction | null }) {
  if (!action) return <span className="crm-chip crm-chip--muted">Aucune</span>;
  return (
    <span className="crm-chip">
      {action.title} · {formatRelativeDay(action.dueAt)}
    </span>
  );
}

function readStoredView(): CrmView {
  try {
    return localStorage.getItem(VIEW_STORAGE_KEY) === "liste" ? "liste" : "kanban";
  } catch {
    return "kanban";
  }
}
